import { AxiosError, AxiosInstance, AxiosInterceptorManager, AxiosRequestConfig, AxiosResponse } from 'axios';
import jwtDecode from 'jwt-decode';

import { CredentialsProvider, refreshSession } from 'modules/Session';
import { JWTPayload } from 'types/api';

export const getDecodedAccessToken = (token: string): JWTPayload | null => {
  try {
    return jwtDecode<JWTPayload>(token);
  } catch (e) {
    return null;
  }
};

type RefreshTokenInterceptor = (instance: AxiosInstance) => Parameters<AxiosInterceptorManager<AxiosResponse>['use']>;

export const createAuthInterceptor = (config: AxiosRequestConfig): AxiosRequestConfig => {
  if (config.withCredentials === false) {
    return config;
  } else {
    config.headers = Object.assign(config.headers ?? {}, CredentialsProvider.getAuthorizationHeader());
    return config;
  }
};

export const createRefreshTokenInterceptor: RefreshTokenInterceptor = (instance: AxiosInstance) => [
  (response) => response,
  (error: AxiosError) => {
    const { config } = error;

    // @ts-ignore
    if (!config || config.avoidRefreshToken) {
      return Promise.reject(error);
    }

    // Should not try to refresh the token on unauthenticated requests
    if (CredentialsProvider.isAuthenticated() && error.response?.status === 401) {
      // To avoid infinite loops:
      // If this request fails again we are probably stuck
      // inside a loop since we just refreshed the token.
      // @ts-ignore
      config.avoidRefreshToken = true; // eslint-disable-line no-param-reassign

      return refreshSession().then(() => {
        // Update the authorization headers for this request
        const headers = CredentialsProvider.getAuthorizationHeader();
        Object.assign(config.headers ?? {}, headers);

        return instance(config);
      });
    }

    return Promise.reject(error);
  },
];
